import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Bike, Package, MapPin, Phone, Clock, IndianRupee, CheckCircle, 
  Navigation, LogOut, RefreshCw, User, Star, TrendingUp 
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export function DeliveryDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [partner, setPartner] = useState(null);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [processingOrder, setProcessingOrder] = useState(null);

  useEffect(function() {
    // Try to get partner info from either storage key
    var phone = localStorage.getItem('deliveryPartnerPhone');
    var partnerInfo = localStorage.getItem('fudco_delivery_partner');
    
    if (!phone && partnerInfo) {
      try {
        var parsed = JSON.parse(partnerInfo);
        phone = parsed.phone;
        // Sync the phone for consistent access
        if (phone) {
          localStorage.setItem('deliveryPartnerPhone', phone);
        }
      } catch(e) {}
    }
    
    if (!phone) {
      navigate('/delivery-partner');
      return;
    }
    fetchDashboard(phone);
    // Auto-refresh every 30 seconds
    var interval = setInterval(function() { fetchDashboard(phone); }, 30000);
    return function() { clearInterval(interval); };
  }, [navigate]);

  function fetchDashboard(phone) {
    setLoading(true);
    fetch(API + '/api/delivery/dashboard/' + phone)
      .then(function(r) { 
        if (!r.ok) throw new Error('Not found');
        return r.json(); 
      })
      .then(function(data) {
        setPartner(data.partner);
        setOrders(data.orders || []);
        setStats(data.stats || {});
        setLoading(false);
      })
      .catch(function() {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('deliveryPartnerPhone');
        navigate('/delivery-partner');
      });
  }

  function handleAcceptOrder(orderId) {
    var phone = localStorage.getItem('deliveryPartnerPhone');
    setProcessingOrder(orderId);
    fetch(API + '/api/delivery/orders/' + orderId + '/accept?phone=' + phone, { method: 'POST' })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data.success) {
          toast.success('Order accepted! Head to pickup location.');
          fetchDashboard(phone);
        } else {
          toast.error(data.detail || 'Could not accept order');
        }
        setProcessingOrder(null);
        setShowOrderModal(false);
      })
      .catch(function() {
        toast.error('Failed to accept order');
        setProcessingOrder(null);
      });
  }

  function handleUpdateStatus(orderId, newStatus) {
    var phone = localStorage.getItem('deliveryPartnerPhone');
    setProcessingOrder(orderId);
    fetch(API + '/api/delivery/orders/' + orderId + '/status?status=' + newStatus + '&partner_id=' + partner.id, { method: 'PUT' })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data.success) {
          toast.success('Order status updated!');
          fetchDashboard(phone);
        } else {
          toast.error(data.detail || 'Could not update status');
        }
        setProcessingOrder(null);
        setShowOrderModal(false);
      })
      .catch(function() {
        toast.error('Failed to update status');
        setProcessingOrder(null);
      });
  }

  function handleLogout() {
    localStorage.removeItem('deliveryPartnerPhone');
    localStorage.removeItem('fudco_delivery_partner');
    navigate('/delivery-partner');
  }

  function getStatusBadge(status) {
    var colors = {
      'pending': 'bg-gray-500',
      'confirmed': 'bg-blue-500',
      'preparing': 'bg-amber-500',
      'ready': 'bg-green-500',
      'picked_up': 'bg-purple-500',
      'out_for_delivery': 'bg-indigo-500',
      'delivered': 'bg-emerald-600'
    };
    return <Badge className={colors[status] || 'bg-gray-500'}>{status.replace(/_/g, ' ').toUpperCase()}</Badge>;
  }

  function openGoogleMaps(address) {
    window.open('https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(address), '_blank');
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-yellow-400/30 border-t-yellow-400 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  var availableOrders = orders.filter(function(o) { return o.status === 'ready' && !o.rider_id; });
  var myOrders = orders.filter(function(o) { return o.rider_id === partner.id || o.rider_phone === partner.phone; });
  var activeOrders = myOrders.filter(function(o) { return o.status !== 'delivered'; });
  var completedOrders = myOrders.filter(function(o) { return o.status === 'delivered'; });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-slate-800/80 border-b border-white/10 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-400 rounded-full">
              <Bike className="w-6 h-6 text-black" />
            </div>
            <div>
              <h1 className="text-white font-bold">Fudco Delivery</h1>
              <p className="text-xs text-gray-400">{partner.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={function() { fetchDashboard(partner.phone); }} className="text-white">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={handleLogout} className="text-red-400">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-green-900/50 to-green-800/30 border-green-500/30">
            <CardContent className="p-4 text-center">
              <IndianRupee className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-green-400">₹{stats.todayEarnings || 0}</p>
              <p className="text-xs text-gray-400">Today's Earnings</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-900/50 to-blue-800/30 border-blue-500/30">
            <CardContent className="p-4 text-center">
              <Package className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-blue-400">{stats.todayDeliveries || 0}</p>
              <p className="text-xs text-gray-400">Today's Deliveries</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-900/50 to-amber-800/30 border-amber-500/30">
            <CardContent className="p-4 text-center">
              <Clock className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-amber-400">{stats.pendingPickups || 0}</p>
              <p className="text-xs text-gray-400">Pending Pickups</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-900/50 to-purple-800/30 border-purple-500/30">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-8 h-8 text-purple-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-purple-400">{stats.totalDeliveries || 0}</p>
              <p className="text-xs text-gray-400">Total Deliveries</p>
            </CardContent>
          </Card>
        </div>

        {/* Orders Tabs */}
        <Tabs defaultValue="available" className="w-full">
          <TabsList className="bg-slate-800/50 border border-white/10 w-full grid grid-cols-3">
            <TabsTrigger value="available" className="data-[state=active]:bg-green-600">
              Available ({availableOrders.length})
            </TabsTrigger>
            <TabsTrigger value="active" className="data-[state=active]:bg-blue-600">
              Active ({activeOrders.length})
            </TabsTrigger>
            <TabsTrigger value="completed" className="data-[state=active]:bg-gray-600">
              Completed ({completedOrders.length})
            </TabsTrigger>
          </TabsList>

          {/* Available Orders */}
          <TabsContent value="available" className="mt-4">
            {availableOrders.length === 0 ? (
              <Card className="bg-slate-800/50 border-white/10">
                <CardContent className="p-8 text-center">
                  <Package className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-400">No orders available for pickup right now</p>
                  <p className="text-sm text-gray-500 mt-1">New orders will appear here automatically</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {availableOrders.map(function(order) {
                  return (
                    <Card key={order.id} className="bg-slate-800/50 border-green-500/30 hover:border-green-400/50 cursor-pointer transition-all" onClick={function() { setSelectedOrder(order); setShowOrderModal(true); }}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-white font-bold">#{order.order_number}</span>
                              {getStatusBadge(order.status)}
                            </div>
                            <p className="text-sm text-gray-400 flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {order.restaurant_name || 'Restaurant'}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">{order.items}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-green-400 font-bold">₹{order.amount}</p>
                            <p className="text-xs text-gray-500">Earn ₹30</p>
                          </div>
                        </div>
                        <Button size="sm" className="w-full mt-3 bg-green-600 hover:bg-green-700" onClick={function(e) { e.stopPropagation(); handleAcceptOrder(order.id); }} disabled={processingOrder === order.id}>
                          {processingOrder === order.id ? 'Accepting...' : 'Accept Order'}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Active Orders */}
          <TabsContent value="active" className="mt-4">
            {activeOrders.length === 0 ? (
              <Card className="bg-slate-800/50 border-white/10">
                <CardContent className="p-8 text-center">
                  <Bike className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-400">No active deliveries</p>
                  <p className="text-sm text-gray-500 mt-1">Accept an order to start delivering</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {activeOrders.map(function(order) {
                  return (
                    <Card key={order.id} className="bg-slate-800/50 border-blue-500/30">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-white font-bold">#{order.order_number}</span>
                              {getStatusBadge(order.status)}
                            </div>
                            <p className="text-sm text-amber-300 flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> Pickup: {order.restaurant_name || 'Restaurant'}
                            </p>
                            <p className="text-sm text-green-300 flex items-center gap-1 mt-1">
                              <Navigation className="w-3 h-3" /> Deliver: {order.delivery_address || order.customer_address || 'Customer Address'}
                            </p>
                          </div>
                          <p className="text-green-400 font-bold">₹{order.amount}</p>
                        </div>
                        <div className="flex gap-2">
                          {order.status === 'picked_up' && (
                            <Button size="sm" className="flex-1 bg-indigo-600 hover:bg-indigo-700" onClick={function() { handleUpdateStatus(order.id, 'out_for_delivery'); }} disabled={processingOrder === order.id}>
                              Out for Delivery
                            </Button>
                          )}
                          {order.status === 'out_for_delivery' && (
                            <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={function() { handleUpdateStatus(order.id, 'delivered'); }} disabled={processingOrder === order.id}>
                              <CheckCircle className="w-4 h-4 mr-1" /> Mark Delivered
                            </Button>
                          )}
                          <Button size="sm" variant="outline" className="border-white/20 text-white" onClick={function() { openGoogleMaps(order.delivery_address || order.customer_address || ''); }}>
                            <Navigation className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" className="border-white/20 text-white" onClick={function() { window.location.href = 'tel:' + (order.customer_phone || ''); }}>
                            <Phone className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Completed Orders */}
          <TabsContent value="completed" className="mt-4">
            {completedOrders.length === 0 ? (
              <Card className="bg-slate-800/50 border-white/10">
                <CardContent className="p-8 text-center">
                  <CheckCircle className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-400">No completed deliveries yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {completedOrders.slice(0, 10).map(function(order) {
                  return (
                    <Card key={order.id} className="bg-slate-800/50 border-white/10">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-white font-medium">#{order.order_number}</span>
                            <p className="text-xs text-gray-500">{order.restaurant_name}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-green-400 font-medium">+₹30</p>
                            <p className="text-xs text-gray-500">{new Date(order.updated_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Order Detail Modal */}
      <Dialog open={showOrderModal} onOpenChange={setShowOrderModal}>
        <DialogContent className="bg-slate-800 border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Order Details</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-lg font-bold">#{selectedOrder.order_number}</span>
                {getStatusBadge(selectedOrder.status)}
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-amber-400 mt-0.5" />
                  <div>
                    <p className="text-gray-400">Pickup from</p>
                    <p className="text-white">{selectedOrder.restaurant_name}</p>
                    <p className="text-gray-500 text-xs">{selectedOrder.restaurant_address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Navigation className="w-4 h-4 text-green-400 mt-0.5" />
                  <div>
                    <p className="text-gray-400">Deliver to</p>
                    <p className="text-white">{selectedOrder.customer_name}</p>
                    <p className="text-gray-500 text-xs">{selectedOrder.delivery_address || selectedOrder.customer_address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Package className="w-4 h-4 text-blue-400 mt-0.5" />
                  <div>
                    <p className="text-gray-400">Items</p>
                    <p className="text-white">{selectedOrder.items}</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-green-900/30 rounded-lg">
                <span className="text-gray-400">Order Value</span>
                <span className="text-green-400 font-bold text-lg">₹{selectedOrder.amount}</span>
              </div>
              {selectedOrder.status === 'ready' && !selectedOrder.rider_id && (
                <Button className="w-full bg-green-600 hover:bg-green-700" onClick={function() { handleAcceptOrder(selectedOrder.id); }} disabled={processingOrder === selectedOrder.id}>
                  {processingOrder === selectedOrder.id ? 'Accepting...' : 'Accept Order'}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default DeliveryDashboard;
